import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Avatar,
  Tooltip,
  Button,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  Person as UserIcon,
  SupportAgent as ConsultantIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Description as FileIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  AutoAwesome as BuildPlanIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import api from '../../api/axios';
import ThinkingBubble from './ThinkingBubble';

const ACCEPTED_FILE_TYPES = '.pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.doc,.docx,.txt';
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_FILES = 5;

/**
 * Consultant Panel
 * A discovery conversation (like Cursor's Plan Mode) that happens BEFORE any
 * plan or flow is built. The AI asks clarifying questions about the client's
 * process, sample documents, and desired output, and only proposes building
 * a plan once it believes it understands the requirement well enough — at
 * which point the user can explicitly ask it to build the plan (which runs
 * through the same feasibility checking as the rest of the builder).
 */
const ConsultantPanel = ({ session, onPlanReady }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [buildingPlan, setBuildingPlan] = useState(false);
  const [readyForPlan, setReadyForPlan] = useState(false);
  const [openQuestions, setOpenQuestions] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (session) loadMessages();
  }, [session]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const loadMessages = async () => {
    try {
      const response = await api.get(`/ai-flow-builder/session/${session.sessionId}`);
      const all = response.data.session.messages || [];
      const consultantMessages = all.filter(m => m.metadata?.mode === 'consultant');
      setMessages(consultantMessages);
      const lastAssistant = [...consultantMessages].reverse().find(m => m.role === 'assistant');
      if (lastAssistant?.metadata) {
        setReadyForPlan(!!lastAssistant.metadata.readyForPlan);
        setOpenQuestions(lastAssistant.metadata.openQuestions || []);
      }
    } catch (err) {
      console.error('Error loading consultant messages:', err);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (attachedFiles.length + files.length > MAX_FILES) {
      alert(`You can only attach up to ${MAX_FILES} files.`);
      return;
    }
    const validFiles = files.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" exceeds the 20MB limit.`);
        return false;
      }
      return true;
    });
    setAttachedFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return <ImageIcon fontSize="small" />;
    if (['xlsx', 'xls', 'csv'].includes(ext)) return <ExcelIcon fontSize="small" />;
    if (ext === 'pdf') return <PdfIcon fontSize="small" />;
    return <FileIcon fontSize="small" />;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    const filesToUpload = [...attachedFiles];
    setInputMessage('');
    setAttachedFiles([]);
    setError(null);
    setLoading(true);

    // Optimistically show the user's message
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
      attachedFiles: filesToUpload.map(f => f.name)
    }]);

    try {
      if (filesToUpload.length > 0) {
        const formData = new FormData();
        filesToUpload.forEach((file) => formData.append('sampleFiles', file));
        formData.append('sessionId', session.sessionId);
        await api.post('/ai-flow-builder/upload-samples', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      const response = await api.post('/ai-flow-builder/consultant/message', {
        sessionId: session.sessionId,
        message: userMessage
      });

      const { message: reply, readyForPlan: ready, openQuestions: questions } = response.data;

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: reply,
        timestamp: new Date(),
        metadata: { mode: 'consultant', readyForPlan: ready, openQuestions: questions }
      }]);
      setReadyForPlan(!!ready);
      setOpenQuestions(questions || []);
    } catch (err) {
      console.error('Error sending consultant message:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I ran into an error. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleBuildPlan = async () => {
    setBuildingPlan(true);
    setError(null);
    try {
      const response = await api.post('/ai-flow-builder/consultant/build-plan', {
        sessionId: session.sessionId
      });
      if (onPlanReady) onPlanReady(response.data);
    } catch (err) {
      console.error('Error building plan from consultation:', err);
      setError(err.response?.data?.message || 'Failed to build a plan from this consultation.');
    } finally {
      setBuildingPlan(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendMessage();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Messages Area */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#f5f5f5' }}>
        {messages.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', mt: 4, px: 3 }}>
            <ConsultantIcon sx={{ fontSize: 64, color: '#999', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Consultant Mode
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Tell me about the process you want to automate or the issue you're having.
              Share your notes, attach sample files, and I'll ask questions until I
              understand it well — then I'll offer to build a plan.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', mt: 2 }}>
              <Chip
                label="We receive Purchase Orders by email as PDFs and need the line items in Excel"
                sx={{ cursor: 'pointer' }}
                onClick={() => setInputMessage("We receive Purchase Orders by email as PDFs and need the line items in Excel")}
              />
              <Chip
                label="Our existing flow is extracting the wrong invoice number sometimes"
                sx={{ cursor: 'pointer' }}
                onClick={() => setInputMessage("Our existing flow is extracting the wrong invoice number sometimes")}
              />
            </Box>
          </Box>
        )}

        {messages.map((message, index) => (
          <ConsultantMessageBubble key={index} message={message} />
        ))}

        {loading && (
          <ThinkingBubble
            icon={<ConsultantIcon />}
            avatarColor="#9333ea"
            labels={['Thinking…', 'Coming up with a response…']}
          />
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Ready-for-plan banner */}
      {readyForPlan && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Alert
            severity="success"
            icon={<BuildPlanIcon />}
            action={
              <Button
                size="small"
                variant="contained"
                onClick={handleBuildPlan}
                disabled={buildingPlan}
                startIcon={buildingPlan ? <CircularProgress size={14} color="inherit" /> : <BuildPlanIcon />}
              >
                {buildingPlan ? 'Building…' : 'Build Plan'}
              </Button>
            }
          >
            I have enough to build a plan. This will also check that everything needed is
            actually available before proposing steps.
          </Alert>
        </Box>
      )}

      {!readyForPlan && openQuestions.length > 0 && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Alert severity="info" icon={<LightbulbIcon />} sx={{ py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Still need to know:</Typography>
            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
              {openQuestions.map((q, i) => (
                <li key={i}><Typography variant="caption">{q}</Typography></li>
              ))}
            </ul>
          </Alert>
        </Box>
      )}

      {error && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        </Box>
      )}

      {/* Input Area */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: 'white' }}>
        {attachedFiles.length > 0 && (
          <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {attachedFiles.map((file, index) => (
              <Chip
                key={index}
                icon={getFileIcon(file.name)}
                label={file.name.length > 25 ? file.name.substring(0, 22) + '...' : file.name}
                onDelete={() => handleRemoveFile(index)}
                deleteIcon={<CloseIcon fontSize="small" />}
                size="small"
                sx={{ bgcolor: '#e3f2fd' }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={ACCEPTED_FILE_TYPES}
            multiple
            style={{ display: 'none' }}
          />
          <Tooltip title={`Attach sample files (max ${MAX_FILES}, 20MB each)`}>
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || attachedFiles.length >= MAX_FILES}
              sx={{ color: attachedFiles.length > 0 ? 'primary.main' : 'text.secondary' }}
            >
              <AttachFileIcon />
            </IconButton>
          </Tooltip>

          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Describe the process, issue, or requirement... (⌘+Enter to send)"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            variant="outlined"
            size="small"
          />
          <IconButton
            color="primary"
            onClick={sendMessage}
            disabled={!inputMessage.trim() || loading}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': { bgcolor: '#ccc' }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const ConsultantMessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <Box sx={{ display: 'flex', mb: 2, justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      {!isUser && (
        <Avatar sx={{ bgcolor: '#9333ea', mr: 1 }}>
          <ConsultantIcon />
        </Avatar>
      )}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          maxWidth: '80%',
          bgcolor: isUser ? '#1976d2' : 'white',
          color: isUser ? 'white' : 'inherit'
        }}
      >
        {isUser ? (
          <Box>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{message.content}</Typography>
            {message.attachedFiles && message.attachedFiles.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {message.attachedFiles.map((fileName, idx) => (
                  <Chip key={idx} label={fileName} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ '& p': { m: 0, mb: 1 }, '& p:last-child': { mb: 0 } }}>
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </Box>
        )}
      </Paper>
      {isUser && (
        <Avatar sx={{ bgcolor: '#1976d2', ml: 1 }}>
          <UserIcon />
        </Avatar>
      )}
    </Box>
  );
};

export default ConsultantPanel;
